import { z } from 'zod';

/**
 * Per-Campaign session cursor (II.B.4 / #51; `07-session-experience.md`).
 *
 * A "current session" pointer scoped to a Campaign. Points at the
 * `event` entity that represents the active in-world session — typically
 * an Event with `traits: ['scheduled', 'session']` (the session trait
 * declared by `core` carries `real_world_span`, `summary`,
 * `session_number`; the scheduled trait carries `occurred_at`,
 * `participants`, `location`).
 *
 * `null` means no active session — fresh Campaign, or the GM has
 * closed the most recent session without opening another. The substrate
 * does NOT enforce that the referenced entity exists or carries the
 * `session` trait — that's an advisory the caller can apply with
 * `isSessionEvent` against an `EntityState`. The cursor is *intent*; the
 * op log is truth.
 *
 * Why a discrete module rather than a column on the Campaign shape:
 * - The per-Campaign D1 row is the operational shell, but the session
 *   pointer is replica-and-DO-replicated state that flows through the
 *   same op stream as facts (a future I.B.x ticket will mint a
 *   `campaign.set_session_cursor` op). Decoupling the data shape from
 *   the column avoids one migration when that op lands.
 * - `advanceSessionCursor` is a pure function — easy to test, easy to
 *   reuse in both the DO and the client.
 */
export const SessionCursorSchema = z
  .object({
    /** The Event entity id of the active session, or `null` if none. */
    session_event_id: z.string().min(1).nullable(),
  })
  .strict();
export type SessionCursor = z.infer<typeof SessionCursorSchema>;

/** A freshly-minted Campaign cursor — no active session yet. */
export const EMPTY_SESSION_CURSOR: SessionCursor = { session_event_id: null };

/**
 * Advance the cursor to a Session Event by id. Returns a new cursor;
 * the input is unchanged (immutable).
 *
 * Passing `null` closes the active session (cursor returns to empty).
 */
export function advanceSessionCursor(
  _cursor: SessionCursor,
  next_session_event_id: string | null,
): SessionCursor {
  return { session_event_id: next_session_event_id };
}

/**
 * Lightweight predicate: does the given entity look like a Session Event?
 * Used by callers to validate cursor advancement before applying it.
 * Substrate-level only — the auditor can layer richer checks.
 */
export function isSessionEvent(entity: {
  root: string;
  traits: readonly string[];
}): boolean {
  return entity.root === 'event' && entity.traits.includes('session');
}
